import React, { useState, useMemo, useEffect, useRef, ReactNode } from 'react';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { Config } from '@/config';
import {
  errorModalTextRecoil,
  completeModalRecoil,
  deleteModalRecoil,
  modelAddModalRecoil,
  modelModifyModalRecoil,
} from '@/atom/ModalAtom';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import LS from '@/styles/layoutStyled';
import CS from '@/styles/componentStyled';
import BS from '@/styles/btnStyled';
import IS from '@/styles/inputStyled';
import TitleTextBox from '@/component/TitleTextBox';
import Pagenation from '@/component/Pagenation';
import SearchBtn from '@/component/SearchBtn';
import DeleteModal from '@/modal/DeleteModal';
import ModelAddModal from '@/modal/ModelAddModal';
import { ModelApi } from '@/sdk/ModelApi';
import {
  ModelControllerGetModelsParams,
  DeleteModelDtoTx,
  ModelArray,
} from '@/sdk/data-contracts';
import { modelListRecoil } from '@/atom/ModelAtom';

const Model = () => {
  const modelApi = new ModelApi({ baseURL: Config.API_URL });
  const navigate = useNavigate();

  const setErrorModalText = useSetRecoilState(errorModalTextRecoil);
  const setCompleteModalState = useSetRecoilState(completeModalRecoil);
  const [deleteModalState, setDeleteModalState] =
    useRecoilState(deleteModalRecoil);
  const [modelAddModalState, setModelAddModalState] =
    useRecoilState(modelAddModalRecoil);
  const [modelModifyModalState, setModelModifyModalState] = useRecoilState(
    modelModifyModalRecoil,
  );
  const [modelListState, setModelListState] = useRecoilState(modelListRecoil);

  const [name, setName] = useState<string>('');
  const [distributorName, setDistributorName] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [order, setOrder] = useState<string>('desc');
  const [checkItems, setCheckItems] = useState<number[]>([]);
  const [listLength, setListLength] = useState<number>(0);

  const allCheckBtnRef = useRef<HTMLInputElement>(null);

  const downloadCSV = async (saveFileName: string) => {
    let allList: ModelArray[] | undefined;

    const dto: ModelControllerGetModelsParams = {
      sortBy,
      order,
    };

    try {
      const { data } = await modelApi.modelControllerGetModels(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      if (data.models) allList = data.models;
    } catch (err: any) {}

    if (!allList || allList.length === 0) {
      setErrorModalText('다운로드할 데이터가 없습니다.');
      return;
    }

    const headers = [
      'No',
      '총판명',
      '모델명',
      '데이터 수',
      '디바이스 수',
      '등록자(ID)',
      '등록일',
    ];

    const rows = allList.map((list, index) => [
      index + 1,
      list.distributorName,
      list.name,
      list.dataCount,
      list.deviceCount,
      list.userName,
      list.createdAt,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.join(',')),
    ].join('\n');

    const bom = '\uFEFF';
    const csvWithBom = bom + csvContent;

    const blob = new Blob([csvWithBom], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${saveFileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filterModelList = async () => {
    const dto: ModelControllerGetModelsParams = {
      name,
      distributorName,
      userName,
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
      offset,
      limit,
      sortBy,
      order,
    };

    try {
      const { data } = await modelApi.modelControllerGetModels(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      if (data.models) setModelListState(data.models);
      if (data.totalColumn) {
        setListLength(data.totalColumn);
      } else if (data.totalColumn === 0) {
        setListLength(0);
      }
    } catch (err: any) {}
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCheckItems([]);
    setOffset(0);
    filterModelList();
    if (allCheckBtnRef.current) {
      allCheckBtnRef.current.checked = false;
    }
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLimit(Number(e.target.value));
    setOffset(0);
  };

  const handleOffsetChange = (value: number) => {
    setOffset((value - 1) * limit);
    setCheckItems([]);
    if (allCheckBtnRef.current) {
      allCheckBtnRef.current.checked = false;
    }
  };

  const handleSingleCheck = (checked: boolean, id: number) => {
    if (checked) {
      setCheckItems((prev) => [...prev, id]);
    } else {
      setCheckItems((prev) => prev.filter((el) => el !== id));
    }
  };

  const handleAllCheck = (checked: boolean) => {
    if (checked) {
      const idArray: number[] = [];
      modelListState?.forEach((el) => idArray.push(Number(el.id)));
      setCheckItems(idArray);
    } else {
      setCheckItems([]);
    }
  };

  const handleSelectDeleteNumCheck = () => {
    if (checkItems.length === 0) {
      setErrorModalText('선택된 항목이 없습니다.');
      return;
    }
    setDeleteModalState(true);
  };

  const handleSelectDelete = async () => {
    try {
      const dto: DeleteModelDtoTx = {
        ids: checkItems,
      };
      await modelApi.modelControllerDeleteModel(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      setCompleteModalState(true);
    } catch (err: any) {}
  };

  const [jsonData, setJsonData] = useState<any>({
    title: '',
    description: '',
    encrypted: false,
    properties: {
      data: {
        type: 'object',
        sort: 1,
        loop: true,
        $count: 'count',
        properties: {},
      },
    },
  });

  const handleEditorChange = (value: string | undefined) => {
    try {
      if (value) {
        const updatedJson = JSON.parse(value);
        setJsonData(updatedJson); // 수정된 JSON 상태 업데이트
      }
    } catch (error) {
      console.error('Invalid JSON format');
    }
  };

  useEffect(() => {
    filterModelList();
  }, [limit, offset]);

  return (
    <>
      <CntBox1>
        <TitleTextBox text="모델" />
        <form onSubmit={handleSearch}>
          <SearchOptionBox>
            <InputWrap>
              <InputArea>
                <h2>등록 일자</h2>
                <IS.TextInputType1
                  style={{ width: 145 }}
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    if (endDate !== '') {
                      if (e.target.value > endDate) {
                        setErrorModalText(
                          '시작일은 종료일보다 클 수 없습니다.',
                        );
                        return;
                      }
                    }
                    setStartDate(e.target.value);
                  }}
                />
                <h2>~</h2>
                <IS.TextInputType1
                  style={{ width: 145 }}
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    if (startDate !== '') {
                      if (e.target.value < startDate) {
                        setErrorModalText(
                          '종료일은 시작일보다 클 수 없습니다.',
                        );
                        return;
                      }
                    }
                    setEndDate(e.target.value);
                  }}
                />
                <IS.TextInputType1
                  style={{ width: 136 }}
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="모델명"
                />
                <IS.TextInputType1
                  style={{ width: 136 }}
                  type="text"
                  value={distributorName}
                  onChange={(e) => setDistributorName(e.target.value)}
                  placeholder="총판명"
                />
                <IS.TextInputType1
                  style={{ width: 136 }}
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="등록자"
                />
              </InputArea>
            </InputWrap>
            <SearchBtn />
          </SearchOptionBox>
        </form>
        <TableOpitonBox>
          <h1>
            전체 <span>{listLength.toLocaleString()}</span>
          </h1>
          <div className="right-box">
            <IS.SelectType1>
              <select
                value={limit}
                onChange={handleLimitChange}
                className="selected"
                style={{ width: 153 }}
              >
                <option value={10}>10개씩 보기</option>
                <option value={20}>20개씩 보기</option>
                <option value={30}>30개씩 보기</option>
                <option value={50}>50개씩 보기</option>
              </select>
              <span className="img-box">
                <img src="/img/arrow_bottom_555.svg" alt="" />
              </span>
            </IS.SelectType1>
            <BS.Btn1Type5
              type="button"
              onClick={() => downloadCSV('model_list')}
            >
              Excel 다운로드
              <span className="img-box">
                <img src="/img/ico_down_fff.svg" alt="" />
              </span>
            </BS.Btn1Type5>
            <BS.Btn1Type1
              type="button"
              onClick={() => setModelAddModalState(true)}
            >
              등록
            </BS.Btn1Type1>
            <BS.Btn1Type2 type="button" onClick={handleSelectDeleteNumCheck}>
              선택 삭제
            </BS.Btn1Type2>
          </div>
        </TableOpitonBox>
        <TableBox>
          <table>
            <thead>
              <tr>
                <th>
                  <IS.CheckBoxType1>
                    <input
                      type="checkbox"
                      id="all"
                      onChange={(e) => handleAllCheck(e.target.checked)}
                      ref={allCheckBtnRef}
                      checked={
                        checkItems.length === modelListState?.length &&
                        modelListState?.length !== 0
                          ? true
                          : false
                      }
                    />
                    <label htmlFor="all" className="img-box">
                      <img src="/img/ico_check_1.svg" alt="" />
                    </label>
                  </IS.CheckBoxType1>
                </th>
                <th>No</th>
                <th>총판명</th>
                <th>모델명</th>
                <th>데이터 수</th>
                <th>디바이스 수</th>
                <th>등록자(ID)</th>
                <th>등록일</th>
              </tr>
            </thead>
            <tbody>
              {modelListState?.map((list, index) => (
                <tr key={index}>
                  <td>
                    <IS.CheckBoxType1>
                      <input
                        type="checkbox"
                        id={`cv-${index}`}
                        onChange={(e) =>
                          handleSingleCheck(e.target.checked, Number(list.id))
                        }
                        checked={
                          checkItems.includes(Number(list.id)) ? true : false
                        }
                      />
                      <label htmlFor={`cv-${index}`} className="img-box">
                        <img src="/img/ico_check_1.svg" alt="" />
                      </label>
                    </IS.CheckBoxType1>
                  </td>
                  <td>{(offset + index + 1).toLocaleString()}</td>
                  <td>{list.distributorName}</td>
                  <td
                    className="button-text"
                    onClick={() => navigate(`/model/detail/${list.id}`)}
                  >
                    {list.name}
                  </td>
                  <td>{list.dataCount.toLocaleString()}</td>
                  <td>{list.deviceCount.toLocaleString()}</td>
                  <td>{list.userName}</td>
                  <td>{list.createdAt}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableBox>
        {listLength !== 0 && (
          <Pagenation
            listAllLength={listLength}
            listLimit={limit}
            onCurrentPageChange={handleOffsetChange}
          />
        )}
      </CntBox1>

      {/* <MonacoEditor
        height="100%" // MonacoEditor 높이 설정
        language="json"
        value={JSON.stringify(jsonData, null, 2)} // JSON 데이터를 문자열로 변환
        onChange={handleEditorChange}
        options={{
          selectOnLineNumbers: true,
        }}
      /> */}

      {modelAddModalState && <ModelAddModal />}
      {deleteModalState && <DeleteModal onConfirm={handleSelectDelete} />}
    </>
  );
};

export default Model;

export const CntBox1 = styled(CS.CntBoxType1)`
  width: 100%;
`;

export const SearchOptionBox = styled(CS.SearchOptionBox)`
  width: 100%;
  margin-top: 20px;
`;

export const InputWrap = styled.div`
  gap: 8px;
  display: flex;
  flex-direction: column;
`;

export const InputArea = styled.div`
  gap: 8px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  > h2 {
    font-size: 14px;
    color: #555;
  }
`;

export const ReportBox = styled.div`
  margin-top: 40px;
  display: flex;
  align-items: center;
  gap: 8px;
  > h2 {
    font-size: 14px;
    color: #555;
  }
`;

export const TableOpitonBox = styled(CS.TableOpitonBox)`
  width: 100%;
  margin-top: 20px;
`;

export const TableBox = styled(CS.TableType1)`
  width: 100%;
  margin-top: 16px;
  margin-bottom: 32px;
`;

export const CheckWrap = styled(CS.Gap24Box)`
  margin-left: 16px;
`;
